// Service to fetch items from API
// API client with Spring Security Basic Auth + CSRF support

const isDev = process.env.NODE_ENV === 'development';
const BASE = isDev ? '' : (process.env.REACT_APP_API_BASE_URL || '');
const ITEMS_ENDPOINT = BASE ? `${BASE.replace(/\/$/, '')}/api/items` : '/api/items';

function getBasicAuthHeader() {
  // Prefer env, fallback to localStorage
  const user = process.env.REACT_APP_API_USER ||
    (typeof window !== 'undefined' && window.localStorage.getItem('REACT_APP_API_USER'));
  const pass = process.env.REACT_APP_API_PASSWORD ||
    (typeof window !== 'undefined' && window.localStorage.getItem('REACT_APP_API_PASSWORD'));

  if (user && pass) {
    let encoded;
    if (typeof window !== 'undefined' && window.btoa) {
      encoded = window.btoa(`${user}:${pass}`);
    } else if (typeof Buffer !== 'undefined') {
      encoded = Buffer.from(`${user}:${pass}`).toString('base64');
    }
    return `Basic ${encoded}`;
  }
  return undefined;
}

// Helper to read cookies
const getCookie = (name) => {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const xsrfCookies = document.cookie.split(';')
    .map(c => c.trim())
    .filter(c => c.startsWith(name + '='));
  if (xsrfCookies.length === 0) return null;
  return decodeURIComponent(xsrfCookies[0].split('=')[1]);
};

const getHeaders = () => {
  const headers = { 'Content-Type': 'application/json' };

  // 1. Basic Auth
  const auth = getBasicAuthHeader();
  if (auth) {
    headers['Authorization'] = auth;
  } else {
    console.warn('No Auth Header found! Check REACT_APP_API_USER/PASSWORD');
  }

  // 2. CSRF Token (Spring Security)
  // Spring Security typically sets XSRF-TOKEN cookie
  const xsrfToken = getCookie('XSRF-TOKEN');
  if (xsrfToken) {
    headers['X-XSRF-TOKEN'] = xsrfToken;
  }

  return headers;
};

// Helper to map frontend fields to backend fields
const mapToBackend = (frontendItem) => {
  const backendItem = {};
  if (frontendItem.nombre !== undefined) backendItem.name = frontendItem.nombre;
  if (frontendItem.descripcion !== undefined) backendItem.description = frontendItem.descripcion;
  if (frontendItem.linea !== undefined) backendItem.line = frontendItem.linea;

  // Backend expects List<String> for color
  if (frontendItem.color !== undefined) {
    backendItem.color = Array.isArray(frontendItem.color)
      ? frontendItem.color
      : [frontendItem.color].filter(Boolean);
  }

  if (frontendItem.diametro !== undefined) backendItem.diameter = parseInt(frontendItem.diametro) || 0;
  if (frontendItem.capacidad !== undefined) backendItem.capacity = parseInt(frontendItem.capacidad) || 0;
  if (frontendItem.comensales !== undefined) backendItem.party_size = parseInt(frontendItem.comensales) || 0;
  if (frontendItem.revestimiento !== undefined) backendItem.coating = frontendItem.revestimiento;

  if (frontendItem.precio !== undefined) backendItem.list_price = parseFloat(frontendItem.precio) || 0;
  if (frontendItem.precioNegocio !== undefined) backendItem.business_price = parseFloat(frontendItem.precioNegocio) || 0;
  if (frontendItem.precioPreferencial !== undefined) backendItem.preferential_price = parseFloat(frontendItem.precioPreferencial) || 0;

  if (frontendItem.puntosEssenPlus !== undefined) backendItem.essenPlus_points = parseInt(frontendItem.puntosEssenPlus) || 0;
  if (frontendItem.puntosRegulares !== undefined) backendItem.regular_points = parseInt(frontendItem.puntosRegulares) || 0;
  if (frontendItem.puntosXL !== undefined) backendItem.xl_points = parseInt(frontendItem.puntosXL) || 0;

  if (frontendItem.imagen !== undefined) backendItem.imageURL = frontendItem.imagen;
  if (frontendItem.active !== undefined) backendItem.active = frontendItem.active;
  if (frontendItem.discount !== undefined) backendItem.discount = parseInt(frontendItem.discount) || 0;
  if (frontendItem.discountEndDate !== undefined) backendItem.discountEndDate = frontendItem.discountEndDate;

  return backendItem;
};

export async function fetchItems() {
  const res = await fetch(ITEMS_ENDPOINT, { headers: getHeaders() });
  if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

  const data = await res.json();
  let items = [];
  if (Array.isArray(data)) items = data;
  else if (data && Array.isArray(data.items)) items = data.items;
  else if (data && Array.isArray(data.data)) items = data.data;
  else throw new Error('API response is not an array');

  // Normalize
  const normalizeColor = (str) =>
    String(str || '')
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase();

  return items.map(item => ({
    id: item.id,
    nombre: item.name,
    descripcion: item.description,
    linea: item.line,
    color: Array.isArray(item.color)
      ? item.color.map(normalizeColor)
      : item.color
        ? normalizeColor(item.color)
        : null,
    diametro: item.diameter,
    capacidad: item.capacity,
    comensales: item.party_size,
    revestimiento: item.coating,
    // Keep old field for backwards compatibility
    precio: item.list_price,
    // Add new fields for pricing system
    list_price: item.list_price,
    business_price: item.business_price,
    preferential_price: item.preferential_price,
    // Keep Spanish names for admin forms
    precioNegocio: item.business_price,
    precioPreferencial: item.preferential_price,
    puntosEssenPlus: item.essenPlus_points,
    puntosRegulares: item.regular_points,
    puntosXL: item.xl_points,
    imagen: item.imageURL,
    active: item.active !== false, // Default to true unless explicitly false
    discount: item.discount || 0,
    discountEndDate: item.discountEndDate || ''
  }));
}

export async function createItem(item) {
  const backendItem = mapToBackend(item);
  const res = await fetch(ITEMS_ENDPOINT, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(backendItem)
  });
  if (!res.ok) throw new Error(`Failed to create item: ${res.status}`);
  return true;
}

export async function updateItem(id, updates) {
  const backendUpdates = mapToBackend(updates);
  // Ensure ID is sent in body as requested
  backendUpdates.id = id;

  const res = await fetch(`${ITEMS_ENDPOINT}/${id}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(backendUpdates)
  });
  if (!res.ok) throw new Error(`Failed to update item: ${res.status}`);
  return true;
}

export async function updateItems(updatesArray) {
  // Execute all updates in parallel
  return Promise.all(updatesArray.map(update => updateItem(update.id, update)));
}

export async function deleteItem(id) {
  const res = await fetch(`${ITEMS_ENDPOINT}/${id}`, {
    method: 'DELETE',
    headers: getHeaders()
  });
  if (!res.ok) throw new Error(`Failed to delete item: ${res.status}`);
  return true;
}

export async function getItemById(id) {
  // Fetch all and find to ensure consistent data mapping
  const items = await fetchItems();
  return items.find(i => i.id === id || i.id === parseInt(id));
}

export default fetchItems;